import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { MdAddShoppingCart } from "react-icons/md";
import { LuCalendarPlus } from "react-icons/lu";
import { useMemo, useState } from "react";
import { useContext } from "react";
import { FcOpenedFolder } from "react-icons/fc";
import { useNavigate } from "react-router-dom";
import { DataContext, GroupContext } from "../../lib/contexts";
import { IngredientList } from "./ingredientList";
import { Label } from "../elements/label";
import { MModal } from "../elements/mmodal";
import { ConfirmModal } from "../elements/confirmModal";
import { MButton } from "../elements/mbutton";
import { MIconButton } from "../elements/miconButton";
import { AddToShoppingListPopup } from "../shopping/addToShoppingListPopup";
import { BsFolderPlus } from "react-icons/bs";
import {
  DeleteDishEndpoint,
  Dish,
  Folder,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { ActionName, dishTypeToColor, PLACEHOLDER_URL } from "../../lib/lib";
import { callEndpoint } from "../../lib/functions";
import { useNotifcation } from "../../lib/hooks/useNotification";

interface DishPopupProps {
  dish: Dish;
  visible: boolean;
  onAddToWeekPlan(): void;
  onClose(): void;
  onRemoveFromDay?: { dateString: string; onClick(): void };
  onEditSaveRedirect?: string;
  folders: Folder[];
}

export function DishPopup(props: DishPopupProps) {
  const [showAddToShoppingListPopup, setShowAddToShoppingListPopup] =
    useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { group } = useContext(GroupContext);
  const { setCurrentAction, refreshDishes, refreshFolders, refreshWeekPlan } =
    useContext(DataContext);
  const canManageDish = props.dish.groupShortId === group?.shortId;
  const notify = useNotifcation();

  const navigate = useNavigate();

  const inFolders = useMemo(
    () =>
      props.folders.filter((folder) => folder.dishIds.includes(props.dish._id)),
    [props.dish, props.folders],
  );

  async function deleteDish() {
    if (!canManageDish) {
      return;
    }

    const [err] = await callEndpoint(DeleteDishEndpoint, {
      id: props.dish._id,
    });

    if (err) {
      notify(`Sletning af ret: ${err.error}`);
      return;
    }

    await Promise.all([refreshDishes(), refreshFolders(), refreshWeekPlan()]);
    setShowDeleteConfirm(false);
    props.onClose();
  }

  return (
    <>
      <MModal
        visible={
          props.visible && !showAddToShoppingListPopup && !showDeleteConfirm
        }
        onClose={props.onClose}
        title={props.dish.name}
        content={
          <div>
            <div
              style={{
                backgroundImage: `url(${props.dish.imageUrl}), url(${PLACEHOLDER_URL})`,
                width: "100%",
                height: "160px",
                backgroundSize: "cover",
                backgroundPosition: "center center",
                borderRadius: "12px",
              }}
            />
            <div
              style={{
                marginTop: "16px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Label
                text={props.dish.type}
                color={dishTypeToColor[props.dish.type || ""]}
              />
              <div style={{ color: "gray", marginLeft: "16px" }}>
                {props.dish.time + " min"}
              </div>
            </div>
            {props.dish.description && (
              <pre
                style={{
                  marginTop: "24px",
                  font: "unset",
                  whiteSpace: "pre-line",
                  maxHeight: "200px",
                  overflow: "auto",
                  // boxShadow: "inset 0px -15px 14px -18px grey",
                  fontSize: "14px",
                }}
                className="show-scroller"
              >
                {props.dish.description}
              </pre>
            )}
            {props.dish.ingredients.length !== 0 && (
              <div style={{ marginTop: 16 }}>
                <IngredientList ingredients={props.dish.ingredients} />
              </div>
            )}
            {inFolders.length > 0 && (
              <div
                style={{
                  margin: "16px 0",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {inFolders.map((folder) => (
                  <div
                    key={folder._id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: "#f3f4f6",
                      color: "var(--text-color)",
                      fontSize: 14,
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                    }}
                  >
                    <FcOpenedFolder style={{ fontSize: 18, flexShrink: 0 }} />
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {folder.folderName}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {props.onRemoveFromDay && (
              <div
                style={{
                  margin: "24px 0",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <MButton
                  type="secondary"
                  icon={<CloseOutlined style={{ fontSize: 12 }} />}
                  style={{
                    borderRadius: 50,
                    height: 32,
                    padding: "0 10px",
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 6,
                  }}
                  onClick={props.onRemoveFromDay.onClick}
                >
                  Fjern fra {props.onRemoveFromDay.dateString}
                </MButton>
              </div>
            )}
          </div>
        }
        footer={
          <div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {canManageDish && (
                <MIconButton
                  title="Slet ret"
                  type="danger"
                  icon={<DeleteOutlined />}
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                <MIconButton
                  title="Tilføj til mappe"
                  icon={<BsFolderPlus style={{ marginBottom: "-2px" }} />}
                  onClick={() => {
                    setCurrentAction({
                      action: ActionName.SelectFolderForDish,
                      data: props.dish,
                    });
                    props.onClose();
                    navigate("/catalogue");
                  }}
                />
                {props.dish.ingredients.length > 0 && (
                  <MIconButton
                    title="Overfør til indkøbsliste"
                    icon={<MdAddShoppingCart />}
                    onClick={() => setShowAddToShoppingListPopup(true)}
                  />
                )}
                <MIconButton
                  title="Tilføj til madplan"
                  icon={<LuCalendarPlus />}
                  onClick={props.onAddToWeekPlan}
                />
                {canManageDish && (
                  <MIconButton
                    title="Rediger ret"
                    icon={<EditOutlined />}
                    onClick={() =>
                      navigate(
                        `/upsert-dish/${props.dish._id}${
                          props.onEditSaveRedirect
                            ? `?redirect=${props.onEditSaveRedirect}`
                            : ""
                        }`,
                      )
                    }
                  />
                )}
              </div>
            </div>
          </div>
        }
      />
      <ConfirmModal
        visible={props.visible && showDeleteConfirm}
        title="Slet ret"
        message={
          <>
            Er du sikker på, at du vil slette <strong>{props.dish.name}</strong>
            ?
          </>
        }
        onCancel={() => setShowDeleteConfirm(false)}
        confirmButton={
          <MButton type="danger" icon={<DeleteOutlined />} onClick={deleteDish}>
            Slet
          </MButton>
        }
      />
      <AddToShoppingListPopup
        visible={showAddToShoppingListPopup}
        onClose={() => setShowAddToShoppingListPopup(false)}
        dishIds={[props.dish._id]}
      />
    </>
  );
}
